<template>
  <div
    ref="toolbar"
    class="whiteboard-toolbar prevent-selected"
    aria-label="白板工具栏"
    :class="{
      toolbar__default: !isScreenShare,
      'toolbar-right': toolbarPosition === 'right',
      'app-toolbar': isHXApp,
      'toolbar__screen-share': isScreenShare,
      'whiteboard-toolbar__collapsed': isCollapsed && !isScreenShare,
    }"
    v-drag="isScreenShare"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div
      class="toolbar-wrap drag-handle"
      :class="{
        collapsed: isCollapsed && !isScreenShare,
        collapsedUp: collapsedUp,
      }"
    >
      <div class="toolbar-content" v-if="!collapsedUp">
        <div v-for="tool in tools" :key="tool.key">
          <span v-if="tool.haveDivider" class="divider"></span>
          <div
            class="tool-btn"
            :class="{ active: currentTool === tool.key }"
            @click="handleToolClick(tool)"
            @mouseenter.stop="isHovering = false"
            @mouseleave.stop="isHovering = true"
          >
            <el-tooltip
              popper-class="live-room-tooltip"
              effect="dark"
              :content="
                tool.label + (tool.shortcut ? `(${tool.shortcut})` : '')
              "
              placement="right"
              :disabled="tooltipDisabled || isHXApp"
            >
              <img
                :class="{
                  'is-disabled':
                    (tool.key === 'undo' && !canUndo) ||
                    (tool.key === 'redo' && !canRedo),
                }"
                class="icon"
                :src="require('../../assets/images/' + tool.key + '.png')"
                alt=""
              />
            </el-tooltip>
            <!-- 文本按钮下方显示当前颜色条 -->
            <div
              v-if="tool.key === 'text'"
              class="text-color-bar"
              :style="{ backgroundColor: currentTextColor }"
            ></div>
            <template v-if="tool.menuComponent">
              <div
                class="menu-wrapper"
                :class="{
                  'menu-left': toolbarPosition === 'right' && !isScreenShare,
                }"
                v-show="showMenu && currentMenuComponent === tool.menuComponent"
                @mouseleave="handleMenuMouseLeave"
                @click.stop
              >
                <component
                  :is="tool.menuComponent"
                  class="menu-wrap"
                  :ref="tool.menuComponent"
                  v-on="
                    tool.key === 'text'
                      ? { 'color-change': handleTextColorChange }
                      : {}
                  "
                  @hide-menu="hideMenu"
                />
              </div>
            </template>
          </div>
        </div>
      </div>
      <img
        class="collapsed-up_tool"
        v-if="whiteToolbarKey && collapsedUp"
        :src="currentKeyImg"
        @click="collapsedUp = false"
        alt=""
      />
    </div>
    <div v-if="isScreenShare && isElectron && !collapsedUp" class="hide-up">
      <img
        @click="collapsedUp = true"
        src="../../assets/images/hide-up.png"
        alt=""
      />
    </div>
    <button
      v-if="!isScreenShare"
      class="collapse-btn"
      :class="{ 'show-collapse': isHovering || isCollapsed }"
      :aria-label="isCollapsed ? '展开工具栏' : '收起工具栏'"
      @click="handleCollapse"
    >
      <i
        v-show="
          (toolbarPosition === 'left' && !isCollapsed) ||
          (toolbarPosition === 'right' && isCollapsed)
        "
        class="el-icon-arrow-left"
      ></i>
      <i
        v-show="
          (toolbarPosition === 'left' && isCollapsed) ||
          (toolbarPosition === 'right' && !isCollapsed)
        "
        class="el-icon-arrow-right"
      ></i>
    </button>
  </div>
</template>

<script>
import { mapState, mapMutations } from "vuex";
import EraserMenu from "./EraserMenu.vue";
import TextMenu from "./TextMenu.vue";
import ShapeMenu from "./ShapeMenu.vue";
import PenMenu from "./PenMenu.vue";
import { drag } from "../utils/drag.js";
import ToolboxMenu from "./ToolboxMenu.vue";
// import { uploadLiveLog } from "@saas/components/src/utils/log";
export const isMac = () => {
  return /macintosh|mac os x|macintel/i.test(navigator.userAgent.toLowerCase());
};
export default {
  name: "WhiteBoardToolbar",
  components: {
    PenMenu,
    ShapeMenu,
    TextMenu,
    EraserMenu,
    ToolboxMenu,
  },
  directives: {
    drag,
  },
  props: {
    isMGAppInPad: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tooltipDisabled: false,
      isCollapsed: false,
      isHovering: false,
      currentTool: "pen",
      tools: [], // 工具列表将在 created 钩子中初始化
      shortcutMap: {
        l: "laser",
        p: "pen",
        s: "shape",
        t: "text",
        e: "eraser",
        z: "undo",
        "shift+z": "redo",
      },
      isMac: false,
      showMenu: false, // 控制菜单显示状态
      currentMenuComponent: null, // 当前显示的菜单组件
      menuLeaveTimer: null, // 用于处理菜单离开的定时器
      canUndo: false,
      canRedo: false,
      currentTextColor: "#000", // 新增，文本颜色
      collapsedUp: false,
      isElectron: window.isElectron,
    };
  },
  watch: {
    showMenu(newValue) {
      if (newValue) {
        this.tooltipDisabled = true;
      } else {
        this.tooltipDisabled = false;
      }
    },
    currentTool: {
      handler(val) {
        this.setWhiteToolbarKey(val);
      },
      immediate: true,
    },
    isScreenShare: {
      handler(val) {
        if (val) {
          this.currentTool = "mouse";
          this.whiteBoardService.setMouseTool();
        }
      },
      immediate: true,
    },
    isMGAppInPad: {
      handler(val) {
        if (val) {
          this.tools = this.tools.filter((item) => item.key !== "toolbox");
        }
      },
      immediate: true,
    },
  },
  created() {
    this.isMac = isMac();
    // 动态生成工具列表，撤销/重做快捷键根据平台变化
    const tools = [
      {
        key: "mouse",
        label: "鼠标",
        shortcut: "",
      },
      {
        key: "laser",
        label: "激光笔",
        shortcut: "L",
      },
      {
        key: "pen",
        label: "画笔",
        shortcut: "P",
        menuComponent: "PenMenu",
        haveDivider: true,
      },
      {
        key: "shape",
        label: "形状",
        shortcut: "S",
        menuComponent: "ShapeMenu",
      },
      {
        key: "text",
        label: "文本",
        shortcut: "T",
        menuComponent: "TextMenu",
      },
      {
        key: "eraser",
        label: "橡皮擦",
        shortcut: "E",
        menuComponent: "EraserMenu",
      },
      {
        key: "toolbox",
        label: "工具箱",
        shortcut: "",
        menuComponent: "ToolboxMenu",
      },
      {
        key: "undo",
        label: "撤销",
        shortcut: this.isMac ? "⌘+Z" : "Ctrl+Z",
        haveDivider: true,
      },
      {
        key: "redo",
        label: "重做",
        shortcut: this.isMac ? "⌘+⇧+Z" : "Ctrl+Shift+Z",
      },
    ];
    if (this.role === 3 || this.isMGAppInPad) {
      // 学生端
      this.tools = tools.filter((item) => item.key !== "toolbox");
    } else {
      this.tools = tools;
    }
  },
  mounted() {
    window.addEventListener("keydown", this.handleKeydown);
    this.whiteBoardService.on("canUndoStatus", (data) => {
      this.canUndo = data;
    });
    this.whiteBoardService.on("canRedoStatus", (data) => {
      this.canRedo = data;
    });
    // 添加全局点击事件监听
    window.addEventListener("touchstart", this.handleGlobalClick);
    window.addEventListener("click", this.handleGlobalClick);
    this.$nextTick(() => {
      if (this.$refs.toolbar) {
        const distance = this.getDistanceToParentBottom(this.$refs.toolbar);
        this.setMessageMaxHeight(distance);
        console.log("__distance", distance);
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeydown);
    // 移除全局点击事件监听
    window.removeEventListener("touchstart", this.handleGlobalClick);
    window.removeEventListener("click", this.handleGlobalClick);
  },
  methods: {
    ...mapMutations("live", [
      "setToolbarPosition",
      "setWhiteToolbarKey",
      "setMessageMaxHeight",
    ]),
    // handleToolbarPosition(event, position) {
    //   console.log("==99handleToolbarPosition", position);

    //   this.setToolbarPosition(position);
    // },
    hideMenu() {
      this.showMenu = false;
    },
    // 处理全局点击事件
    handleGlobalClick(event) {
      // 如果子菜单没有显示，不需要处理
      if (!this.showMenu) return;

      // 获取工具栏元素和当前子菜单元素
      const toolbarEl = this.$refs?.toolbar;
      const currentMenuEl = this.currentMenuComponent
        ? this.$refs[this.currentMenuComponent]?.[0]?.$el
        : null;

      // 检查点击是否发生在展开的工具栏主区域或当前打开的菜单内部
      const isClickInToolbar = toolbarEl?.contains(event.target);
      const isClickInSubmenu = currentMenuEl?.contains(event.target);

      // 如果点击发生在展开的工具栏主区域或子菜单内部，不处理
      if (isClickInToolbar || isClickInSubmenu) {
        return;
      }
      // 其他所有情况都关闭子菜单
      this.showMenu = false;
      this.currentMenuComponent = null;
    },
    handleToolClick(tool) {
      const { key, menuComponent, keydown } = tool;
      // uploadLiveLog({
      //   data: {
      //     component: "Toolbar",
      //     action: "click_tool_click",
      //     tool: tool,
      //   },
      //   type: "click",
      // });
      if (menuComponent) {
        if (!keydown) {
          // 如果点击的是当前工具，则切换菜单显示状态
          if (this.currentTool === key) {
            this.showMenu = !this.showMenu;
            if (this.showMenu) {
              this.setCurrentMenuComponentValue(menuComponent);
            } else {
              this.currentMenuComponent = null;
            }
            return;
          } else {
            // 如果点击的是新工具，则显示新工具的菜单
            this.showMenu = !!menuComponent;
            this.setCurrentMenuComponentValue(menuComponent);
          }
        }
        this.currentTool = key;
        if (this.$refs[menuComponent]?.[0]?.handleStyleChange) {
          this.$refs[menuComponent][0].handleStyleChange();
        }
      } else {
        if (this.currentTool === key) return;
        this.showMenu = false;
        if (!["undo", "redo"].includes(key)) {
          this.currentTool = key;
        }
        switch (key) {
          case "mouse":
            this.whiteBoardService.setMouseTool();
            break;
          // case "eraser":
          //   this.whiteBoardService.setEraserTool();
          //   break;
          case "laser":
            this.whiteBoardService.setLaserTool();
            break;
          case "undo":
            this.whiteBoardService.undo();
            break;
          case "redo":
            this.whiteBoardService.redo();
            break;
          default:
            break;
        }
      }
    },
    setCurrentMenuComponentValue(menuComponent) {
      if (menuComponent === "EraserMenu") {
        if (this.role !== 3) {
          // 学生端不展示橡皮子菜单
          this.currentMenuComponent = menuComponent;
          if (this.$refs?.[menuComponent]?.[0]?.handleStyleChange) {
            this.$refs[menuComponent][0].handleStyleChange();
          }
        } else {
          this.showMenu = false;
        }
      } else {
        this.currentMenuComponent = menuComponent;
      }
    },
    handleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },
    handleKeydown(e) {
      // 撤销/重做快捷键根据平台判断
      if (
        (this.isMac &&
          e.metaKey &&
          !e.shiftKey &&
          e.key.toLowerCase() === "z") ||
        (!this.isMac && e.ctrlKey && !e.shiftKey && e.key.toLowerCase() === "z")
      ) {
        this.handleToolClick({ key: "undo", keydown: true });
        e.preventDefault();
        return;
      }
      if (
        (this.isMac &&
          e.metaKey &&
          e.shiftKey &&
          e.key.toLowerCase() === "z") ||
        (!this.isMac && e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "z")
      ) {
        this.handleToolClick({ key: "redo", keydown: true });
        e.preventDefault();
        return;
      }
      if (
        e.target.tagName === "INPUT" ||
        e.target.tagName === "TEXTAREA" ||
        e.target.className.includes("ql-editor")
      )
        return;
      const key = e.key.toLowerCase();
      if (["l", "p", "s", "t", "e"].includes(key)) {
        const tool = this.tools.find(
          (tool) => tool.key === this.shortcutMap[key]
        );
        this.handleToolClick({ ...tool, keydown: true });
        e.preventDefault();
      }
    },
    // 处理菜单鼠标移出事件
    handleMenuMouseLeave() {
      if (this.isHXApp) return;
      // 清除之前的定时器
      if (this.menuLeaveTimer) {
        clearTimeout(this.menuLeaveTimer);
      }
      // 设置新的定时器，添加小延迟避免菜单闪烁
      this.menuLeaveTimer = setTimeout(() => {
        this.showMenu = false;
        this.currentMenuComponent = null;
      }, 100);
    },
    handleTextColorChange(color) {
      this.currentTextColor = color;
    },
    handleMouseEnter(e) {
      // 检查鼠标是否在工具栏的非功能区
      const target = e.target;
      const isToolBtn = target.closest(".tool-btn");
      const isMenuWrapper = target.closest(".menu-wrapper");

      // 如果鼠标在工具按钮或菜单上，不显示收起按钮
      if (isToolBtn || isMenuWrapper) {
        this.isHovering = false;
        return;
      }

      this.isHovering = true;
    },
    handleMouseLeave() {
      this.isHovering = false;
    },
    /**
     * 获取元素底部距离父元素底部的距离
     * @param {HTMLElement} element - 目标元素
     * @returns {number} 距离（单位：px），如果没有父元素则返回 0
     */
    getDistanceToParentBottom(element) {
      // 检查元素是否存在
      if (!element || !(element instanceof HTMLElement)) {
        console.error("请传入有效的DOM元素");
        return 0;
      }

      // 获取父元素（直接父节点）
      const parentElement = element.parentElement;
      if (!parentElement) {
        console.warn("元素没有父元素");
        return 0;
      }

      // 获取元素和父元素的布局信息（相对于父元素的位置）
      const elementRect = element.getBoundingClientRect();
      const parentRect = parentElement.getBoundingClientRect();

      // 计算元素底部相对于父元素底部的距离
      // 公式：父元素高度 - （元素底部 - 父元素顶部）
      const parentHeight = parentRect.height;
      const elementBottomRelativeToParent = elementRect.bottom - parentRect.top;
      const distance = parentHeight - elementBottomRelativeToParent;

      // 返回取整后的距离（避免小数像素）
      return Math.round(distance);
    },
  },
  computed: {
    ...mapState("live", [
      "whiteBoardService",
      "toolbarPosition",
      "isHXApp",
      "screenShareStatus",
      "whiteToolbarKey",
    ]),
    ...mapState("whiteBoard", ["role"]),
    isScreenShare() {
      if (["start", "pause"].includes(this.screenShareStatus)) {
        if (this.$refs.toolbar) {
          this.$refs.toolbar.style.removeProperty("transform");
        }
        return true;
      } else {
        return false;
      }
    },
    currentKeyImg() {
      return require("../../assets/images/" + this.whiteToolbarKey + ".png");
    },
  },
};
</script>

<style lang="scss" scoped>
.whiteboard-toolbar {
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 9;
  width: auto;
  &.toolbar-right {
    right: 20px;
    left: auto;
    .collapse-btn {
      left: 0;
      transform: translateY(-50%) translateX(-100%);
    }
  }
  .toolbar-wrap {
    padding: 9px;
    display: flex;
    flex-direction: column;
    align-items: center;
    transition: width 0.2s ease;

    width: 46px;
    background: #ffffff;
    box-shadow: 0px 4px 10px 0px rgba(20, 20, 43, 0.04);
    border-radius: 10px 10px 10px 10px;
    border: 1px solid $live-room-border-color;
    &.collapsed {
      width: 0;
      overflow: hidden;
      padding: 0;
      border: none;
      transition: width 0.2s ease;
    }
    &.collapsedUp {
      overflow: hidden;
      padding: 0;
      border: none;
      cursor: pointer;
      width: 28px;
      height: 28px;
      background: #bedaff;
      border-radius: 6px 6px 6px 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      img {
        width: 20px;
        height: 20px;
      }
    }
  }

  .toolbar-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 46px;
    & > div {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }

  .tool-btn {
    position: relative;
    margin: 3px 0;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    width: 28px;
    height: 28px;
    border-radius: 6px 6px 6px 6px;

    &:hover,
    &:focus {
      background: $live-room-hover-bg;
    }
    &.active {
      background: $live-room-active-bg;
    }

    .icon {
      width: 20px;
      height: 20px;
      &.is-disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .menu-wrapper {
      position: absolute;
      left: 46px;
      z-index: 11;
      top: 0;
      background: transparent;
      &.menu-left {
        left: auto;
        right: 46px;
      }
    }
    .shape-menu-component {
      transform: translate(0px, -70px);
    }
  }
  .collapse-btn {
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    transition: all 0.2s;
    width: 18px;
    height: 42px;
    background: rgba(63, 63, 70, 0.4);
    border-radius: 12px 12px 12px 12px;
    color: #fff;
    opacity: 0;
    pointer-events: none;
    i {
      font-weight: bold;
      font-size: 14px;
    }

    &.show-collapse {
      opacity: 1;
      pointer-events: auto;
    }
  }
  .hide-up {
    width: 30px;
    height: 30px;
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%) translateY(100%);
    cursor: pointer;
    img {
      width: 100%;
      cursor: pointer;
      height: 100%;
    }
  }
  .divider {
    width: 21px;
    height: 1px;
    background: $live-room-border-color;
    margin: 3px 0;
  }
  .text-color-bar {
    width: 16px;
    height: 2px;
    border-radius: 2px;
    margin: 0 auto;
    margin-top: 2px;
    position: absolute;
    bottom: 3px;
  }
}
.whiteboard-toolbar__collapsed {
  left: 0;
  &.toolbar-right {
    right: 20px;
  }
}
.toolbar__default {
  transform: translate(0, -50%) !important;
  &.toolbar-right {
    .collapse-btn {
      transform: translateY(-50%) translateX(-100%) !important;
    }
  }
}
.toolbar__screen-share {
  width: 46px !important;
  left: 20px !important;
  top: 50% !important;
  transform: translate(0, -50%);
}
// app
.app-toolbar.whiteboard-toolbar {
  .collapse-btn {
    opacity: 1;
    pointer-events: auto;
  }
  .shape-menu-component {
    transform: translate(0px, -33%);
  }
}
</style>
